import React, { useEffect, useRef, useState } from 'react'
import { Canvas, FabricObject, Rect } from 'fabric'
import { resolveThemeColor, withAlpha } from './themeColors'
import { BASE_PPI, installTransformGuard } from './canvasTransforms'
import { makeRotationHandleVisible } from './rotationHandle'

export { BASE_PPI, polygonsOverlap, validateArtworkTransform } from './canvasTransforms'
export const SAFETY_MARGIN_OBJECT_NAME = 'safety-margin'

const SAFETY_MARGIN_IN = 0.125
const SAFETY_STROKE_WIDTH_PX = 2
const SAFETY_DASH_PX = [8, 5]
const CANVAS_SIZE_PRECISION = 4
const ZOOM_EPSILON = 0.0001

function normalizeCanvasSize(value) {
  const factor = 10 ** CANVAS_SIZE_PRECISION
  return Math.round(Number(value) * factor) / factor
}

function syncSafetyMargin(
  canvas,
  canvasWidthPx,
  canvasHeightPx,
  showSafetyMargin,
  horizontalSafetyMarginIn,
  zoom
) {
  const existing = canvas.getObjects().find(obj => obj.name === SAFETY_MARGIN_OBJECT_NAME)
  if (!showSafetyMargin) {
    if (existing) canvas.remove(existing)
    return
  }

  const horizontalMargin = horizontalSafetyMarginIn * BASE_PPI
  const verticalMargin = SAFETY_MARGIN_IN * BASE_PPI
  const safeZoom = Math.max(Number(zoom) || 1, 0.01)
  const attrs = {
    left: horizontalMargin,
    top: verticalMargin,
    originX: 'left',
    originY: 'top',
    width: canvasWidthPx - horizontalMargin * 2,
    height: canvasHeightPx - verticalMargin * 2,
    fill: 'transparent',
    stroke: resolveThemeColor('--color-danger'),
    strokeWidth: SAFETY_STROKE_WIDTH_PX / safeZoom,
    strokeDashArray: SAFETY_DASH_PX.map(length => length / safeZoom),
    selectable: false,
    evented: false,
    name: SAFETY_MARGIN_OBJECT_NAME,
  }

  if (existing) {
    if (typeof existing.set === 'function') {
      existing.set(attrs)
    } else {
      Object.assign(existing, attrs)
    }
    existing.name = SAFETY_MARGIN_OBJECT_NAME
    existing.setCoords?.()
    canvas.bringObjectToFront?.(existing)
    return
  }

  const safetyRect = new Rect(attrs)
  safetyRect.name = SAFETY_MARGIN_OBJECT_NAME
  canvas.add(safetyRect)
  canvas.bringObjectToFront?.(safetyRect)
}

export default function SheetCanvas({
  sheetSize,
  maxArtworkHeightIn,
  zoom = 1.0,
  showSafetyMargin = true,
  showTransparencyGrid = true,
  showPlacementGrid = false,
  isActive = true,
  frozen = false,
  navigationOnly = false,
  onCanvasReady,
  onChange,
  onTransformValidationChange,
  isProgrammaticCanvasResizeRef,
}) {
  const canvasEl = useRef(null)
  const fabricRef = useRef(null)
  const [canvasReady, setCanvasReady] = useState(false)
  const localProgrammaticResizeRef = useRef(false)
  const programmaticResizeRef = isProgrammaticCanvasResizeRef || localProgrammaticResizeRef
  const appliedCanvasSizeRef = useRef({ width: null, height: null })
  const appliedZoomRef = useRef(null)
  const appliedSafetyMarginRef = useRef(null)
  const currentSheetSizeRef = useRef(sheetSize)
  const interactionDisabled = frozen || navigationOnly

  const canvasWidthPx = sheetSize.width_in * BASE_PPI
  const canvasHeightPx = sheetSize.height_in * BASE_PPI
  const horizontalSafetyMarginIn = Number(
    sheetSize.horizontal_safety_margin_in ?? SAFETY_MARGIN_IN
  )

  useEffect(() => {
    currentSheetSizeRef.current = {
      ...sheetSize,
      height_in: maxArtworkHeightIn || sheetSize.height_in,
    }
  }, [sheetSize, maxArtworkHeightIn])

  useEffect(() => {
    const displayWidth = normalizeCanvasSize(canvasWidthPx * zoom)
    const displayHeight = normalizeCanvasSize(canvasHeightPx * zoom)
    let canvas

    const previousResizeState = programmaticResizeRef.current
    programmaticResizeRef.current = true
    try {
      canvas = new Canvas(canvasEl.current, {
        width: displayWidth,
        height: displayHeight,
        backgroundColor: 'transparent',
        preserveObjectStacking: true,
        uniformScaling: true,
        uniScaleKey: null,
      })
      canvas.setZoom(zoom)
      fabricRef.current = canvas

      const accent = resolveThemeColor('--color-accent')
      canvas.set({
        selectionColor: withAlpha(accent, 0.12),
        selectionBorderColor: accent,
      })
      FabricObject.ownDefaults.borderColor = accent
      FabricObject.ownDefaults.cornerColor = resolveThemeColor('--color-base')
      FabricObject.ownDefaults.cornerStrokeColor = accent
      FabricObject.ownDefaults.transparentCorners = false

      syncSafetyMargin(
        canvas,
        canvasWidthPx,
        canvasHeightPx,
        showSafetyMargin,
        horizontalSafetyMarginIn,
        zoom
      )
      appliedCanvasSizeRef.current = { width: displayWidth, height: displayHeight }
      appliedZoomRef.current = zoom
      appliedSafetyMarginRef.current = showSafetyMargin
    } finally {
      programmaticResizeRef.current = previousResizeState
    }

    // Canvas handles preserve the artwork's current proportions. Deliberate
    // stretching remains available by unlocking width/height in the sidebar.
    const protectArtworkShape = object => {
      if (!object || object.name === SAFETY_MARGIN_OBJECT_NAME) return
      // Fabric supports optional angle snapping per object. Clear it explicitly
      // for restored artwork and active selections so the handle always follows
      // the pointer continuously.
      if (typeof object.set === 'function') object.set({ snapAngle: 0, snapThreshold: 0 })
      else Object.assign(object, { snapAngle: 0, snapThreshold: 0 })
      object.setControlsVisibility?.({ ml: false, mr: false, mt: false, mb: false })
      makeRotationHandleVisible(object)
    }
    canvas.on('object:added', ({ target }) => protectArtworkShape(target))
    canvas.on('selection:created', () => protectArtworkShape(canvas.getActiveObject?.()))
    canvas.on('selection:updated', () => protectArtworkShape(canvas.getActiveObject?.()))

    // Let Fabric's handle rotate continuously around its fixed anchor. Rewriting
    // the angle after its rotation event causes snapping and anchor drift.
    // Invalid placements are still rejected by the guard when the edit ends.
    installTransformGuard(canvas, () => currentSheetSizeRef.current, onTransformValidationChange)

    if (onChange) {
      const handleObjectChange = (...args) => {
        if (!programmaticResizeRef.current) onChange(...args)
      }
      canvas.on('object:modified', handleObjectChange)
      canvas.on('object:added', handleObjectChange)
      canvas.on('object:removed', handleObjectChange)
    }

    if (onCanvasReady) {
      onCanvasReady(canvas)
    }
    setCanvasReady(true)

    return () => {
      setCanvasReady(false)
      canvas.dispose()
      fabricRef.current = null
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Freeze editing during layout changes and mobile navigation. Artwork is
  // added from several call sites after mount, so re-apply on object:added
  // rather than only when the interaction mode changes —
  // otherwise the next placement arrives fully interactive.
  useEffect(() => {
    const canvas = fabricRef.current
    if (!canvas) return undefined

    const applyInteractivity = () => {
      canvas.selection = !interactionDisabled
      const objects = canvas.getObjects?.() || []
      objects.forEach(object => {
        if (!object || object.name === SAFETY_MARGIN_OBJECT_NAME) return
        if (typeof object.set === 'function') {
          object.set({ selectable: !interactionDisabled, evented: !interactionDisabled })
        } else {
          object.selectable = !interactionDisabled
          object.evented = !interactionDisabled
        }
      })
      if (interactionDisabled && canvas.getActiveObject?.()) canvas.discardActiveObject?.()
      canvas.requestRenderAll?.()
    }

    const rejectDisabledSelection = () => {
      if (!interactionDisabled || !canvas.getActiveObject?.()) return

      canvas.discardActiveObject?.()
      canvas.requestRenderAll?.()
    }

    applyInteractivity()
    canvas.on?.('object:added', applyInteractivity)
    // Fabric fires object:added before placement callers can activate the new
    // object. Guard selection events too so a late arrival cannot restore
    // transform handles after the object:added freeze has run.
    canvas.on?.('selection:created', rejectDisabledSelection)
    canvas.on?.('selection:updated', rejectDisabledSelection)
    return () => {
      canvas.off?.('object:added', applyInteractivity)
      canvas.off?.('selection:created', rejectDisabledSelection)
      canvas.off?.('selection:updated', rejectDisabledSelection)
    }
  }, [canvasReady, interactionDisabled])

  // Update Fabric zoom when zoom prop changes (after initial mount)
  useEffect(() => {
    const canvas = fabricRef.current
    if (!canvas) return
    const width = normalizeCanvasSize(canvasWidthPx * zoom)
    const height = normalizeCanvasSize(canvasHeightPx * zoom)
    const appliedSize = appliedCanvasSizeRef.current
    const dimensionsChanged = width !== appliedSize.width || height !== appliedSize.height
    const zoomChanged = appliedZoomRef.current === null ||
      Math.abs(appliedZoomRef.current - zoom) >= ZOOM_EPSILON
    const safetyMarginChanged = appliedSafetyMarginRef.current !== showSafetyMargin

    if (!dimensionsChanged && !zoomChanged && !safetyMarginChanged) return

    const previousResizeState = programmaticResizeRef.current
    programmaticResizeRef.current = true
    try {
      if (dimensionsChanged) {
        canvas.setDimensions({ width, height })
        appliedCanvasSizeRef.current = { width, height }
      }
      if (zoomChanged) {
        canvas.setZoom(zoom)
        appliedZoomRef.current = zoom
      }
      if (dimensionsChanged || zoomChanged || safetyMarginChanged) {
        syncSafetyMargin(
          canvas,
          canvasWidthPx,
          canvasHeightPx,
          showSafetyMargin,
          horizontalSafetyMarginIn,
          zoom
        )
        appliedSafetyMarginRef.current = showSafetyMargin
      }
      canvas.requestRenderAll()
    } finally {
      programmaticResizeRef.current = previousResizeState
    }
  }, [zoom, canvasWidthPx, canvasHeightPx, showSafetyMargin, horizontalSafetyMarginIn])

  const wrapperClasses = [
    'canvas-wrapper',
    navigationOnly ? 'canvas-wrapper--navigation' : '',
    showTransparencyGrid ? 'canvas-transparency-grid' : '',
    showPlacementGrid ? 'canvas-placement-grid' : '',
    !isActive ? 'canvas-wrapper--inactive' : '',
  ]
    .filter(Boolean)
    .join(' ')

  // Match the rulers' sheet origin and zoom. At extreme zoom-out, show whole-
  // inch multiples rather than merging one-inch lines into a solid gray sheet.
  const inchPx = BASE_PPI * zoom
  const gridIntervalIn = [1, 2, 5, 10].find(interval => interval * inchPx >= 8) || 10

  return (
    <div
      className={wrapperClasses}
      style={{ '--canvas-grid-spacing': `${normalizeCanvasSize(inchPx * gridIntervalIn)}px` }}
      data-testid="sheet-canvas-wrapper"
    >
      <canvas
        ref={canvasEl}
        data-testid="fabric-canvas"
      />
    </div>
  )
}
